import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useApp } from "context/app";
import { useNotifications } from "context/notifications";

import { ReactComponent as MenuIcon } from "../assets/icons/menu.svg";
import { ReactComponent as CloseIcon } from "../assets/icons/close.svg";
import { ReactComponent as AddTaskIcon } from "../assets/icons/add-task.svg";
import { ReactComponent as NotificationsIcon } from "../assets/icons/notifications.svg";
import { ReactComponent as TalkIcon } from "../assets/icons/talk.svg";
import { ReactComponent as SettingsIcon } from "../assets/icons/settings.svg";
import { ReactComponent as LogoutIcon } from "../assets/icons/logout.svg";
import { ReactComponent as HomeIcon } from "../assets/icons/home.svg";
import { ReactComponent as WebsiteIcon } from "../assets/icons/website.svg";
import { ReactComponent as TasksIcon } from "../assets/icons/tasks.svg";
import { ReactComponent as WalletIcon } from "../assets/icons/wallet.svg";
import { ReactComponent as StarIcon } from "../assets/icons/star.svg";
import { ReactComponent as BulbIcon } from "../assets/icons/bulb.svg";
import { ReactComponent as QuestionmarkIcon } from "../assets/icons/questionmark.svg";
import { ReactComponent as ContactIcon } from "../assets/icons/contact.svg";
import { ReactComponent as ClientsIcon } from "../assets/icons/clients.svg";
import { ReactComponent as WebmastersIcon } from "../assets/icons/webmasters.svg";
import { ReactComponent as UsersIcon } from "../assets/icons/users.svg";

const useMenuSettings = () => {
  const navigate = useNavigate();
  const {
    user,
    current_website,
    websites,
    logout,
    setSendNotificationOpen,
    setCookie,
    setCreateTaskOpen,
  } = useApp();
  const { data } = useNotifications();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [submenu, setSubmenu] = useState(null);

  useEffect(() => {
    const className = "menu-mobile-open";
    document.body.classList.toggle(className, isOpen);
    return () => document.body.classList.remove(className);
  }, [isOpen]);

  const unreaded_count = data?.reduce((a, b) => a + (!!!b?.status ? 1 : 0), 0);

  const isClient = user?.type === "client";
  const isAdmin = user?.type === "admin";
  const isWebmaster = user?.type === "webmaster";

  const dock = useMemo(
    () => [
      {
        icon: <MenuIcon />,
        label: t("Menu"),
        onClick: () => {
          setIsOpen((prev) => !prev);
          setCreateTaskOpen(false);
          setSendNotificationOpen(false);
          setSubmenu(null);
        },
        change: {
          icon: <CloseIcon />,
          label: t("Close"),
        },
      },
      {
        icon: !isWebmaster ? <AddTaskIcon /> : <TasksIcon />,
        label: t(!isWebmaster ? "Add task" : "Tasks"),
        href: !isAdmin ? `/tasks${!isWebmaster ? "/new" : ""}` : undefined,
        onClick: isAdmin
          ? () => {
              setIsOpen(false);
              setSendNotificationOpen(false);
              setCreateTaskOpen(true);
            }
          : undefined,
      },
      {
        icon: <NotificationsIcon />,
        label: t("Notifications"),
        badge: unreaded_count > 0 ? unreaded_count : null,
        href: "/notifications",
        replace: {
          icon: <SettingsIcon />,
          label: t("Settings"),
          href: "/settings",
        },
      },
      {
        icon: isAdmin || isWebmaster ? <NotificationsIcon /> : <TalkIcon />,
        label: isAdmin || isWebmaster ? t("Send") : t("Contact"),
        onClick: () => {
          if (isAdmin || isWebmaster) {
            setCreateTaskOpen(false);
            setSendNotificationOpen(true);
          } else {
            navigate("/contact");
          }
        },
        replace: {
          icon: <LogoutIcon />,
          label: t("Logout"),
          onClick: () => logout(),
        },
      },
    ],
    [
      t,
      navigate,
      logout,
      unreaded_count,
      isAdmin,
      isWebmaster,
      setSendNotificationOpen,
      setCreateTaskOpen,
    ],
  );

  const nav = [
    {
      icon: <HomeIcon />,
      label: t("Dashboard"),
      href: "/",
    },
    isClient && {
      icon: <WebsiteIcon />,
      label: t("Websites"),
      submenu: websites?.length > 0,
      href: websites?.length === 0 ? "/websites/new" : undefined,
      onClick:
        websites?.length > 0
          ? () =>
              setSubmenu({
                label: t("Websites"),
                items: [
                  ...websites.map(({ _id, website }) => ({
                    icon: <WebsiteIcon />,
                    label: `${website}${current_website?._id === _id ? " (aktywna)" : ""}`,
                    active: current_website?._id === _id,
                    href: `/websites/${_id}`,
                    onClick: () => {
                      setCookie(`${user?._id}-website`, _id, {
                        maxAge: 1707109200,
                      });
                    },
                  })),
                  {
                    type: "button",
                    label: t("Add another website"),
                    href: "/websites/new",
                  },
                ],
              })
          : undefined,
    },
    isAdmin && {
      icon: <ClientsIcon />,
      label: t("Clients"),
      href: "/clients",
    },
    {
      icon: <TasksIcon />,
      label: t("Tasks"),
      href: "/tasks",
    },
    isClient && {
      icon: <WalletIcon />,
      label: t("Payments"),
      href: "/invoices",
    },
    isAdmin && {
      icon: <WebmastersIcon />,
      label: t("Webmasters"),
      href: "/webmasters",
    },
    isAdmin && {
      icon: <UsersIcon />,
      label: t("Users"),
      href: "/users",
    },
    {
      icon: <NotificationsIcon />,
      label: t("Notifications"),
      badge: unreaded_count > 0 ? unreaded_count : null,
      href: "/notifications",
    },
    !isWebmaster && {
      icon: <StarIcon />,
      label: t("Referral program"),
      href: "/referral-program",
    },
    isClient && {
      icon: <BulbIcon />,
      label: t("Need a new project?"),
      href: "/new-project",
    },
    isClient && {
      icon: <TalkIcon />,
      label: t("Help"),
      href: websites?.length === 0 ? "/contact" : undefined,
      inactive: websites?.length === 0,
      submenu: websites?.length > 0,
      onClick:
        websites?.length > 0
          ? () =>
              setSubmenu({
                label: t("Help"),
                items: [
                  {
                    icon: <QuestionmarkIcon />,
                    label: t("FAQ"),
                    href: "/faq",
                  },
                  {
                    icon: <ContactIcon />,
                    label: t("Contact"),
                    href: "/contact",
                  },
                ],
              })
          : undefined,
    },
  ].filter(Boolean);

  return {
    dock,
    nav,
    submenu,
    isOpen,
    setIsOpen,
    setSubmenu,
    setSendNotificationOpen,
  };
};

export default useMenuSettings;
